import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  CreateMarketplaceFacilitatorDocument,
  MarketplaceFacilitatorStatusDocument,
  UpdateMarketplaceFacilitatorDocument,
  type MarketplaceFacilitatorStatusQuery,
  type MutationCreateMarketplaceFacilitatorArgs,
  type MutationUpdateMarketplaceFacilitatorArgs,
} from "@/graphql/generated";
import { adminGqlRequest } from "@/lib/graphql";
import { getErrorMessage } from "@/lib/graphqlError";

export type FacilitatorStatus =
  MarketplaceFacilitatorStatusQuery["marketplaceFacilitatorStatus"][number];

const SAVE_FAILED = "Could not save. Please try again.";

export function useMarketplaceFacilitators(workspaceId: string) {
  const queryClient = useQueryClient();
  const queryKey = ["marketplaceFacilitatorStatus", workspaceId];

  const { data, isError } = useQuery({
    queryKey,
    queryFn: async () =>
      (
        await adminGqlRequest<MarketplaceFacilitatorStatusQuery>(
          MarketplaceFacilitatorStatusDocument,
          { workspaceId },
        )
      ).marketplaceFacilitatorStatus,
  });

  const save = async (
    document: string,
    variables:
      | MutationCreateMarketplaceFacilitatorArgs
      | MutationUpdateMarketplaceFacilitatorArgs,
  ): Promise<string | null> => {
    try {
      await adminGqlRequest(document, variables);
      await queryClient.invalidateQueries({ queryKey });
      return null;
    } catch (err) {
      return getErrorMessage(err, SAVE_FAILED);
    }
  };

  return {
    rows: data ?? [],
    loadFailed: isError,
    mark: (marketplaceCode: string) =>
      save(CreateMarketplaceFacilitatorDocument, {
        input: { marketplaceCode, facilitatorWorkspaceId: workspaceId },
      }),
    remove: (markedId: string) =>
      save(UpdateMarketplaceFacilitatorDocument, {
        input: { id: markedId, status: "INACTIVE" },
      }),
  };
}
